import {
    consoleLogHandler,
    getSearchParams,
    initializeLogging as initializeVinylLogging,
    LogLevel,
    parseLogLevel,
    setLogLevel,
} from '@amazon/vinyl-util'
import { debug } from '@/core/debug'
import { startLogStream } from '@/core/logStream'
import { installLogRedaction } from '@/redactSensitive'

/** localStorage key persisting the console log-level preference. */
export const LOG_LEVEL_STORAGE_KEY = 'logLevel'

/**
 * Defensive read so private-browsing / SSR does not throw.
 *
 * Kept as raw `localStorage` (not a validated `createPersistentItem`): this
 * feeds the synchronous boot-time log-level setup, which runs before first
 * paint, so the persistent item's async `get()` can't serve it.
 */
export function readStoredLogLevel(): string | null {
    try {
        return typeof localStorage !== 'undefined'
            ? localStorage.getItem(LOG_LEVEL_STORAGE_KEY)
            : null
    } catch {
        return null
    }
}

/** Persist the console log-level preference. Defensive — storage may be blocked. */
export function writeStoredLogLevel(level: string): void {
    try {
        localStorage.setItem(LOG_LEVEL_STORAGE_KEY, level)
    } catch {
        // Private-browsing / storage-blocked fallback: silently swallow.
    }
}

/**
 * Resolve the console log level. Precedence: `?logLevel=` query param wins,
 * else the persisted `logLevel` value. With neither set, debug sessions
 * default to DEBUG and everything else to WARN — matching vinyl-util's
 * console default.
 */
function resolveConsoleLogLevel(): LogLevel {
    const param = getSearchParams().get('logLevel')
    const fromParam = param ? parseLogLevel(param) : null
    if (fromParam != null) return fromParam
    const stored = readStoredLogLevel()
    const fromStored = stored ? parseLogLevel(stored) : null
    if (fromStored != null) return fromStored
    return debug ? LogLevel.DEBUG : LogLevel.WARN
}

/**
 * Apply the resolved console log level to the console sink. Called at boot and
 * again when the debug panel toggle flips, so a mid-session change takes effect
 * without a reload.
 *
 * Sets the console sink level only — not the main gate (`setLogLevel`). The one
 * exception is VERBOSE: it sits below the main gate's DEBUG default, so verbose
 * events would be dropped before reaching the console sink unless the main gate
 * is raised too.
 */
export function applyConsoleLogLevel(): void {
    const level = resolveConsoleLogLevel()
    const cLH = consoleLogHandler.value
    if (cLH) cLH.logLevel = level
    if (level === LogLevel.VERBOSE) setLogLevel(LogLevel.VERBOSE)
}

export function initializeLogging(): void {
    installLogRedaction()
    initializeVinylLogging()
    applyConsoleLogLevel()
    startLogStream()
}
